// src/modules/auth/AuthProvider.tsx
import React, { createContext, useState, useEffect, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "../../services/auth.service";
import { authUtils } from "./utils/auth.utils";
import { useError } from "../../errors/useError";
import { User } from "./types";
import { handleAuthError } from "./utils/auth.error";
import { LoadingScreen } from "./components/LoadingScreen";
import { toast } from "react-toastify";
import { LoginOptions, BasicRegisterData } from "./hooks/useAuth";
import {
  businessService,
  CreateBusinessRequest,
} from "../../services/business.service";

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
}

interface AuthContextType extends AuthState {
  login: (
    email: string,
    password: string,
    options?: LoginOptions
  ) => Promise<void>;
  logout: () => Promise<void>;
  register: (values: BasicRegisterData) => Promise<void>;
  changePassword: (
    currentPassword: string,
    newPassword: string
  ) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    loading: true,
  });

  const { throwError } = useError();
  const navigate = useNavigate();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const accessToken = authUtils.getAccessToken();
        const refreshToken = authUtils.getRefreshToken();

        if (!accessToken || !refreshToken) {
          setState({ user: null, isAuthenticated: false, loading: false });
          return;
        }

        try {
          const response = await authService.getCurrentUser();
          const user: User = response;
          setState({
            user,
            isAuthenticated: true,
            loading: false,
          });
        } catch (error) {
          // toast.error(
          //   `Error fetching user, attempting token refresh... ${error}`
          // );

          if (refreshToken) {
            try {
              const response = await authService.refreshToken(refreshToken);

              authUtils.setTokens(response.accessToken, response.refreshToken);

              // Retry getting user with new token
              const userResponse = await authService.getCurrentUser();
              const user: User = userResponse;

              setState({
                user,
                isAuthenticated: true,
                loading: false,
              });
            } catch (refreshError) {
              authUtils.clearTokens();
              setState({
                user: null,
                isAuthenticated: false,
                loading: false,
              });
              navigate("/auth/login");
            }
          }
        }
      } catch (error) {
        authUtils.clearTokens();
        setState({
          user: null,
          isAuthenticated: false,
          loading: false,
        });
        navigate("/auth/login");
      }
    };

    initializeAuth();
  }, [navigate]);

  const login = async (
    email: string,
    password: string,
    options?: LoginOptions
  ) => {
    try {
      setState((prev) => ({ ...prev, loading: true }));

      // Get the current path to check if user is coming from cashier login
      const isFromCashierLogin =
        window.location.pathname.includes("/auth/cashier-login") ||
        options?.isPosLogin === true;

      let response;
      if (isFromCashierLogin) {
        // Use PIN login for cashier login
        response = await authService.loginWithPin(email, password);
      } else {
        // Use regular login for other cases
        response = await authService.login({ email, password });
      }

      const { accessToken, refreshToken, user } = response;

      await authUtils.setTokens(accessToken, refreshToken);

      setState({
        user,
        isAuthenticated: true,
        loading: false,
      });

      // Route based on user role, permissions, and login source
      setTimeout(() => {
        if (
          (user.is_pos_user === true &&
            isFromCashierLogin &&
            user.is_waiter === true) ||
          options?.isPosLogin
        ) {
          navigate("/pos/waiterDashboard");
        }
        if (
          (user.is_pos_user === true && isFromCashierLogin) ||
          options?.isPosLogin
        ) {
          navigate("/pos");
        } else if (user.role === "SUPER_ADMIN") {
          navigate("/super");
        } else if (
          user.role &&
          user.permissions &&
          user.permissions.length > 0
        ) {
          navigate("/app-launcher");
        } else {
          navigate("/auth/login");
        }
      }, 100); // Small delay to ensure tokens are set
    } catch (error) {
      setState((prev) => ({ ...prev, loading: false }));
      const authError = handleAuthError(error);
      throwError(authError);
      throw authError;
    }
  };

  const register = async (values: BasicRegisterData) => {
    try {
      // Step 1: Create the business
      const businessData: CreateBusinessRequest = {
        business_name: values.business_name,
        owner: {
          first_name: values.owner.first_name,
          last_name: values.owner.last_name,
          email: values.owner.email,
          password: values.owner.password,
        },
        settings: {
          currency: "BHD",
          language: "en",
          timezone: "Asia/Bahrain",
          tax_settings: {
            tax_included: true,
            default_tax_rate: 10,
          },
        },
      };

      const businessResponse = await businessService.createBusiness(
        businessData
      );

      // Step 2: Register the user
      const userData = {
        email: values.owner.email,
        password: values.owner.password,
        first_name: values.owner.first_name,
        last_name: values.owner.last_name,
        role: "store_owner",
        store_id: businessResponse.data.business.id,
        is_store_owner: true,
        permissions: [
          "view_products",
          "edit_products",
          "view_orders",
          "edit_orders",
        ],
        confirmPassword: values.owner.password,
        business_name: values.business_name,
      };

      const userResponse = await authService.register(userData);

      // Set initial user state from responses
      setState({
        ...state,
        user: {
          id: businessResponse.data.owner.id,
          email: values.owner.email,
          first_name: values.owner.first_name,
          last_name: values.owner.last_name,
          role: "store_owner",
          is_store_owner: true,
          store_id: businessResponse.data.business.id,
          permissions: [
            "view_products",
            "edit_products",
            "view_orders",
            "edit_orders",
          ],
        },
        isAuthenticated: true, // Set to true since user is now registered
      });

      // Store the tokens from user registration
      if (userResponse.accessToken && userResponse.refreshToken) {
        authUtils.setTokens(
          userResponse.accessToken,
          userResponse.refreshToken
        );
      }
    } catch (error) {
      console.error("Registration error in AuthProvider:", error);
      handleAuthError(error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      const refreshToken = authUtils.getRefreshToken();
      if (refreshToken) {
        await authService.logout(refreshToken);
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear all tokens and storage
      authUtils.clearTokens();

      // Reset auth state
      setState({
        user: null,
        isAuthenticated: false,
        loading: false,
      });

      // Navigate to login page
      navigate("/auth/login");

      // Force reload the page to clear any cached state
      window.location.reload();
    }
  };

  const changePassword = async (
    currentPassword: string,
    newPassword: string
  ) => {
    try {
      await authService.changePassword(currentPassword, newPassword);
      throwError({
        name: "Success",
        message: "Password changed successfully",
        type: "AUTHENTICATION",
      });
    } catch (error) {
      const authError = handleAuthError(error);
      throwError(authError);
      throw authError;
    }
  };

  const value = {
    ...state,
    login,
    logout,
    register,
    changePassword,
  };

  if (state.loading) {
    return <LoadingScreen />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
